import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../utils/helpers";
import { cleanDocumentationSvg, documentationDiffMarks, generateIflowDocumentationPdf } from "../utils/iflowDocumentationPdf";
import { documentationSections, documentationExtensionFacts, orderedDocumentationExtensions } from "../utils/iflowDocumentationPresentation";
import { parseBpmnLayout } from "../utils/bpmnShared";
import IflowDiagram from "./IflowDiagram";
import "./IflowDocumentation.css";

// This index uses local persisted documents, independently of the SAP content cache.
export function useIflowDocumentationIndex(instanceId, enabled, packageId) {
  const [result, setResult] = useState({ instanceId: null, documents: [], loading: true, error: false });
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(n => n + 1), []);
  useEffect(() => {
    window.addEventListener("focus", refresh);
    window.addEventListener("iflow-documentation-saved", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("iflow-documentation-saved", refresh);
    };
  }, [refresh]);
  useEffect(() => {
    if (!enabled || !instanceId) return;
    let active = true;
    const controller = new AbortController();
    setResult({ instanceId, documents: [], loading: true, error: false });
    api(`/api/${encodeURIComponent(instanceId)}/content/documentation`, { signal: controller.signal })
      .then(data => {
        if (!Array.isArray(data.documents)) throw new Error("Invalid documentation index");
        if (active) setResult({ instanceId, documents: data.documents, loading: false, error: false });
      })
      .catch(() => { if (active) setResult({ instanceId, documents: [], loading: false, error: true }); });
    return () => { active = false; controller.abort(); };
  }, [instanceId, enabled, packageId, revision]);
  return { ...(result.instanceId === instanceId ? result : { documents: [], loading: true, error: false }), refresh };
}

function styleLabel(doc, t) {
  const key = `doku:stil.${doc.style}.titel`;
  const name = doc.styleTitle || t(key, { defaultValue: doc.style });
  return `${name} · ${doc.language === "en" ? "EN" : "DE"}`;
}

export function IflowDocumentationButton({ documents, flowName, onOpen }) {
  const { t } = useTranslation(["content", "doku"]);
  if (!documents?.length) return <span className="iflow-doc-empty">{t("dokumente.keine")}</span>;
  const label = t("dokumente.oeffnen", { name: flowName });
  if (documents.length === 1) return <button type="button" className="btn btn--secondary btn--sm iflow-doc-control"
    aria-label={label} onClick={event => { event.stopPropagation(); onOpen(documents[0]); }}>
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 3H5v18h14V8zM14 3v5h5M8 12h8M8 16h6" /></svg>
    {t("dokumente.knopf")}
  </button>;
  return <select className="iflow-doc-control iflow-doc-select" aria-label={label} value=""
    onClick={event => event.stopPropagation()} onChange={event => {
      event.stopPropagation();
      const doc = documents.find(item => item.id === event.target.value);
      if (doc) onOpen(doc);
    }}>
    <option value="" disabled>{t("dokumente.stilWaehlen")}</option>
    {documents.map(doc => <option key={doc.id} value={doc.id}>{styleLabel(doc, t)}</option>)}
  </select>;
}

function DocumentationMarkdown({ markdown, subordinate = false }) {
  const html = useMemo(() => {
    const clean = DOMPurify.sanitize(marked.parse(String(markdown || "")), {
      ALLOWED_TAGS: ["p", "br", "strong", "em", "del", "blockquote", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "table", "thead", "tbody", "tr", "th", "td", "pre", "code", "hr", "a", "img"],
      ALLOWED_ATTR: ["href", "alt", "start"],
    });
    const parsed = new DOMParser().parseFromString(clean, "text/html");
    // Remote images must not leak document content or trigger external requests.
    parsed.querySelectorAll("img").forEach(image => image.replaceWith(parsed.createTextNode(image.getAttribute("alt") || "")));
    parsed.querySelectorAll("a").forEach(link => {
      if (!/^https?:\/\//i.test(link.getAttribute("href") || "")) link.removeAttribute("href");
      else { link.setAttribute("target", "_blank"); link.setAttribute("rel", "noopener noreferrer"); }
    });
    if (subordinate) parsed.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach(heading => {
      const replacement = parsed.createElement("h4");
      replacement.append(...heading.childNodes); heading.replaceWith(replacement);
    });
    return parsed.body.innerHTML;
  }, [markdown, subordinate]);
  return <div className="iflow-doc-markdown" dangerouslySetInnerHTML={{ __html: html }} />;
}

function DocumentationDiagram({ xml, svg, caption, marks, t }) {
  const prepared = useMemo(() => {
    try {
      if (xml) return Object.keys(parseBpmnLayout(xml).shapes).length ? { xml } : null;
      const safe = cleanDocumentationSvg(svg);
      return safe ? { src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(safe)}` } : null;
    } catch { return null; }
  }, [xml, svg]);
  return <figure className="iflow-doc-diagram">
    {caption && <figcaption>{caption}</figcaption>}
    {!prepared ? <p className="iflow-doc-note">{t(xml || svg ? "diagramUnavailable" : "missingDiagram")}</p>
      : prepared.xml ? <IflowDiagram bpmnXml={prepared.xml} height={300} marks={marks} />
        : <img src={prepared.src} alt={caption || t("title")} />}
  </figure>;
}

function DocumentationBase({ base, t }) {
  const blocks = useMemo(() => {
    const diagrams = [...(base.bpmnSvgs || [])];
    for (const entry of base.bpmnXmls || []) {
      if (!diagrams.some(item => item.name === entry.name)) diagrams.push({ name: entry.name, xml: entry.xml });
    }
    diagrams.push(...(base.svgs || []));
    const normal = value => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const marker = /\[\[\s*DIAGRAMM\s*:\s*([^\]|]+)(?:\|([^\]]*))?\]\]/gi;
    const used = new Set(), parts = [];
    const markdown = String(base.markdown || "");
    let cursor = 0, match;
    while ((match = marker.exec(markdown))) {
      parts.push({ markdown: markdown.slice(cursor, match.index) });
      const id = normal(match[1].replace(/^bpmn\s*:/i, ""));
      const index = diagrams.findIndex((item, n) => !used.has(n) && normal(item.id || item.name) === id);
      if (index >= 0) used.add(index);
      parts.push({ diagram: diagrams[index] || {}, caption: match[2]?.trim() || diagrams[index]?.name });
      cursor = marker.lastIndex;
    }
    parts.push({ markdown: markdown.slice(cursor) });
    diagrams.forEach((diagram, index) => { if (!used.has(index)) parts.push({ diagram, caption: diagram.name || diagram.title }); });
    return parts;
  }, [base]);
  return <section className="iflow-doc-base">{!base.markdown ? <p className="iflow-doc-note">{t("empty")}</p> : blocks.map((part, index) => part.diagram
    ? <DocumentationDiagram key={index} {...part.diagram} caption={part.caption} t={t} />
    : <DocumentationMarkdown key={index} markdown={part.markdown} />)}</section>;
}

function DocumentationProgress({ progress, t }) {
  const [now, setNow] = useState(Date.now);
  const phase = progress?.phase || "queued";
  useEffect(() => {
    if (phase === "failed") return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [phase]);
  const completed = progress?.completedSteps || 1;
  const measuredUntil = ["retry", "failed"].includes(phase) ? progress?.updatedAt || progress?.startedAt || now : now;
  const elapsed = progress?.startedAt ? Math.max(0, Math.floor((measuredUntil - progress.startedAt) / 1000)) : null;
  return <div className="iflow-doc-progress">
    <div className="iflow-doc-progress-head"><strong role="status">{t(`generation.phase.${phase}`)}</strong>
      {phase !== "failed" && <span>{t("generation.steps", { completed, total: 3 })}</span>}</div>
    {phase !== "failed" && <><progress max={3} value={completed} aria-label={t("generation.label")} />
      <p>{t("generation.milestones")}</p></>}
    {phase === "failed" && <p>{t(`generation.reason.${progress?.reason || "generation"}`)} {t("generation.stopped")}</p>}
    {phase === "retry" && <p>{t("generation.publicationRetry")}</p>}
    {phase === "unavailable" && <p>{t("generation.unavailable")}</p>}
    {elapsed !== null && <p className="iflow-doc-progress-time">{t("generation.elapsed", { minutes: Math.floor(elapsed / 60), seconds: String(elapsed % 60).padStart(2, "0") })}
    </p>}
  </div>;
}

function DocumentationExtension({ extension, number, language, t }) {
  const [open, setOpen] = useState(false);
  const facts = useMemo(() => documentationExtensionFacts(extension, language), [extension, language]);
  const presentation = useMemo(() => {
    try { return { sections: documentationSections(extension, language) }; }
    catch { return { error: true }; }
  }, [extension, language]);
  const comparison = useMemo(() => {
    if (!open || !extension.beforeBpmn || !extension.afterBpmn) return null;
    try { return documentationDiffMarks(extension.beforeBpmn, extension.afterBpmn); }
    catch { return null; }
  }, [open, extension.beforeBpmn, extension.afterBpmn]);
  return <details className="iflow-doc-extension" onToggle={event => setOpen(event.currentTarget.open)}>
    <summary><span>{t("transport", { number })}</span>{facts[0] && <span className="iflow-doc-extension-meta">{facts[0]}</span>}
      {["pending", "failed"].includes(extension.analysisStatus) && <span className="iflow-doc-extension-meta">{t(`generation.phase.${extension.generationProgress?.phase || "queued"}`)}</span>}</summary>
    {open && <div className="iflow-doc-extension-body">
      {["pending", "failed"].includes(extension.analysisStatus) && <DocumentationProgress progress={extension.generationProgress} t={t} />}
      {facts.length > 0 && <ul className="iflow-doc-facts">{facts.map(fact => <li key={fact}>{fact}</li>)}</ul>}
      {extension.content == null && <p className="iflow-doc-note">{t("legacyNotice")}</p>}
      {presentation.error ? <p role="alert">{t("invalidExtension")}</p> : presentation.sections.filter(section => !["pending", "failed"].includes(extension.analysisStatus) || section.key === "change").map(section => <section key={section.key} data-section={section.key}>
        <h3>{section.title}</h3><DocumentationMarkdown markdown={section.markdown} subordinate />
      </section>)}
      <section data-section="comparison"><h3>{t("section.comparison")}</h3><p className="iflow-doc-note">{t("snapshotNote")}</p>
        <div className="iflow-doc-comparison">
          <DocumentationDiagram xml={extension.beforeBpmn} caption={t("before")} marks={comparison?.marksBefore} t={t} />
          <DocumentationDiagram xml={extension.afterBpmn} caption={t("after")} marks={comparison?.marksAfter} t={t} />
        </div>
        <p className="iflow-doc-note">{t("legend")}</p>
        {comparison && !Object.keys(comparison.marksBefore).length && !Object.keys(comparison.marksAfter).length && <p className="iflow-doc-note">{t("noDiagramChanges")}</p>}
      </section>
    </div>}
  </details>;
}

function DocumentationReader({ document: doc }) {
  const language = doc.language || "de";
  const { t } = useTranslation("iflowDocumentationPdf", { lng: language });
  const extensions = useMemo(() => orderedDocumentationExtensions(doc.extensions), [doc.extensions]);
  return <article className="iflow-doc-reader" lang={language}>
    <p className="iflow-doc-note">{t("aiNotice")}</p>
    <DocumentationBase base={doc.base || {}} t={t} />
    {Boolean(doc.restrictedExtensionsCount) && <p className="iflow-doc-note">{t("restrictedExtensions", { count: doc.restrictedExtensionsCount })}</p>}
    {extensions.length > 0 && <section className="iflow-doc-extensions"><h2>{t("extensions")}</h2>
      {extensions.map((extension, index) => <DocumentationExtension key={extension.id || index} extension={extension} number={index + 1} language={language} t={t} />)}
    </section>}
  </article>;
}

export function IflowDocumentationPreview({ instanceId, document: selected, documents, onClose, onSelect }) {
  const { t, i18n } = useTranslation(["content", "gemeinsam", "doku"]);
  const titleId = useId();
  const dialog = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [pdf, setPdf] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [view, setView] = useState("document");
  const [pdfError, setPdfError] = useState(false);
  const [error, setError] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const previous = window.document.activeElement;
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = "hidden";
    dialog.current?.querySelector(".iio-modal-close")?.focus();
    const handleKey = event => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close.current(); }
      if (event.key !== "Tab") return;
      const items = Array.from(dialog.current?.querySelectorAll("button:not(:disabled),a[href],select,iframe,summary,[tabindex='0']") || []).filter(item =>
        item.tabIndex !== -1 && !item.closest("[hidden]") && (!item.closest("details:not([open])") || item.tagName === "SUMMARY"));
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && window.document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && window.document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.document.addEventListener("keydown", handleKey);
    return () => {
      window.document.removeEventListener("keydown", handleKey);
      window.document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => {
    let active = true, url, timer, busy = false, fingerprint = null, lastDocument = null;
    const controller = new AbortController();
    setPdf(null); setLoaded(null); setError(false); setPdfError(false); setRefreshError(false);
    const refresh = async (initial = false) => {
      if (!active || busy) return;
      clearTimeout(timer);
      if (!initial && window.document.visibilityState === "hidden") {
        timer = setTimeout(refresh, 10000); return;
      }
      busy = true;
      try {
        const doc = await api(`/api/${encodeURIComponent(instanceId)}/content/flows/${encodeURIComponent(selected.flowId)}/documentation/${encodeURIComponent(selected.id)}`, { signal: controller.signal });
        if (!active) return;
        setError(false); setRefreshError(false); lastDocument = doc;
        setLoaded({ id: selected.id, instanceId, doc });
        // Progress is transient API metadata, not document content. It must not
        // rebuild the PDF or reset its native viewer every time a phase changes.
        const nextFingerprint = JSON.stringify({ ...doc, extensions: doc.extensions?.map(({ generationProgress, ...extension }) => extension) });
        if (nextFingerprint === fingerprint) return;
        fingerprint = nextFingerprint;
        // Keep the reader mounted: open chapters, focus and scroll position survive.
        // Never offer an obsolete PDF as the current document after an update.
        setPdf(null); setPdfError(false);
        if (url) { URL.revokeObjectURL(url); url = null; }
        try {
          const blob = await generateIflowDocumentationPdf(doc, { language: doc.language || i18n.language, signal: controller.signal });
          if (!active) return;
          url = URL.createObjectURL(blob);
          setPdf({ id: selected.id, instanceId, url });
        } catch { if (active) setPdfError(true); }
      } catch (err) {
        if (!active) return;
        if ([401, 403, 404].includes(err?.status)) {
          lastDocument = null; fingerprint = null; setLoaded(null); setPdf(null);
          if (url) { URL.revokeObjectURL(url); url = null; }
        }
        if (lastDocument) setRefreshError(true); else setError(true);
      } finally {
        busy = false;
        if (active) timer = setTimeout(refresh, lastDocument?.extensions?.some(e => e.analysisStatus === "pending") ? 3000 : 30000);
      }
    };
    const visible = () => { if (window.document.visibilityState === "visible") refresh(); };
    window.document.addEventListener("visibilitychange", visible);
    refresh(true);
    return () => { active = false; clearTimeout(timer); controller.abort(); window.document.removeEventListener("visibilitychange", visible); if (url) URL.revokeObjectURL(url); };
  }, [instanceId, selected.id, selected.flowId, i18n.language, retry]);
  const currentPdf = pdf?.id === selected.id && pdf?.instanceId === instanceId ? pdf : null;
  const currentDocument = loaded?.id === selected.id && loaded?.instanceId === instanceId ? loaded.doc : null;
  const changeTab = (next, focus = false) => {
    setView(next);
    if (focus) dialog.current?.querySelector(`[data-view="${next}"]`)?.focus();
  };
  const fileName = `${selected.flowName || selected.flowId}-${selected.style}`.replace(/[^\p{L}\p{N}._-]/gu, "_") + ".pdf";
  return createPortal(<div className="iio iio-modal-overlay is-open iflow-doc-overlay" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialog} className="iio iio-modal iflow-doc-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header className="iio-modal-head iflow-doc-header">
        <div className="iio-modal-head-left"><span className="iflow-doc-kicker">{t("dokumente.vorschau")}</span><h2 id={titleId} title={selected.flowName || selected.flowId}>{selected.flowName || selected.flowId}</h2></div>
        <div className="iflow-doc-variant">
        {documents.length > 1 ? <select aria-label={t("dokumente.stil")} title={styleLabel(selected, t)} className="iflow-doc-control iflow-doc-select" value={selected.id} onChange={event => onSelect(documents.find(doc => doc.id === event.target.value))}>
          {documents.map(doc => <option key={doc.id} value={doc.id}>{styleLabel(doc, t)}</option>)}
        </select> : <span title={styleLabel(selected, t)}>{styleLabel(selected, t)}</span>}
        </div>
      <div className="iflow-doc-tabs" role="tablist" aria-label={t("dokumente.ansicht")} onKeyDown={event => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault(); changeTab(event.key === "Home" ? "document" : event.key === "End" ? "pdf" : view === "document" ? "pdf" : "document", true);
      }}>
        {["document", "pdf"].map(tab => <button key={tab} type="button" role="tab" data-view={tab} id={`${titleId}-${tab}-tab`} aria-controls={`${titleId}-${tab}`} aria-selected={view === tab} tabIndex={view === tab ? 0 : -1} onClick={() => changeTab(tab)}>{t(tab === "document" ? "dokumente.dokument" : "dokumente.pdf")}</button>)}
      </div>
        <div className="iflow-doc-download">{currentPdf && <a href={currentPdf.url} download={fileName}>
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5" /></svg>
          <span>{t("dokumente.herunterladen")}</span>
        </a>}</div>
        <button type="button" className="iio-modal-close" aria-label={t("gemeinsam:aktion.schliessen")} onClick={onClose}><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
      </header>
      <div className="iflow-doc-preview iflow-doc-document-panel" role="tabpanel" id={`${titleId}-document`} aria-labelledby={`${titleId}-document-tab`} hidden={view !== "document"} aria-busy={!currentDocument && !error}>
        {refreshError && <p className="iflow-doc-note" role="status">{t("dokumente.aktualisierungFehler")}</p>}
        {error ? <div className="iflow-doc-feedback" role="alert"><p>{t("dokumente.listeFehler")}</p><button type="button" className="btn btn--secondary" onClick={() => setRetry(n => n + 1)}>{t("dokumente.erneut")}</button></div>
          : currentDocument ? <DocumentationReader key={`${instanceId}-${selected.id}`} document={currentDocument} />
            : <div className="iflow-doc-feedback" role="status"><span className="iio-spinner" aria-hidden="true" />{t("dokumente.dokumentLaedt")}</div>}
      </div>
      <div className="iflow-doc-preview" role="tabpanel" id={`${titleId}-pdf`} aria-labelledby={`${titleId}-pdf-tab`} hidden={view !== "pdf"} aria-busy={!currentPdf && !error && !pdfError}>
        {refreshError && <p className="iflow-doc-note" role="status">{t("dokumente.aktualisierungFehler")}</p>}
        {error || pdfError ? <div className="iflow-doc-feedback" role="alert"><p>{t("dokumente.vorschauFehler")}</p><button type="button" className="btn btn--secondary" onClick={() => setRetry(n => n + 1)}>{t("dokumente.erneut")}</button></div>
          : currentPdf ? <iframe src={currentPdf.url} title={t("dokumente.pdfTitel", { name: selected.flowName || selected.flowId })} />
            : <div className="iflow-doc-feedback" role="status"><span className="iio-spinner" aria-hidden="true" />{t("dokumente.pdfLaedt")}</div>}
      </div>
    </section>
  </div>, window.document.body);
}
